import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { EmptyState } from 'src/components/empty-state'
import { AssistantHint } from 'src/components/assistant-hint'
import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { Button } from 'src/components/button'
import { CustomSelect } from 'src/components/custom-select'
import { ConfirmDeleteDialog } from 'src/components/confirm-delete-dialog'
import { ContentPane } from 'src/components/layout/content-pane'
import { FilterPane } from 'src/components/layout/filter-pane'
import { MainContent } from 'src/components/layout/main-content'
import { PageHeader } from 'src/components/layout/page-header'
import { useDeleteWithConfirmation } from 'src/hooks/use-delete-with-confirmation'
import { exportTemplatesApi } from 'src/services/export-templates'
import { exportTemplateQueries } from 'src/services/export-templates/queries'
import { organizationQueries } from 'src/services/user'
import { SelectOption } from 'src/services/base-model'
import { ArrowLeftIcon, PlusIcon } from '@heroicons/react/24/outline'
import { ItemCollection } from 'src/components/layout/item-collection'
import { ListToolbar } from 'src/components/layout/list-toolbar'
import { ExportTemplateItem } from 'src/components/export-templates/export-template-item'
import { useViewMode } from 'src/hooks/use-view-mode'

export const ExportTemplatesPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [organization, setOrganization] = useState<SelectOption[]>([])
  const [search, setSearch] = useState('')
  const [viewMode, setViewMode] = useViewMode('export-templates', 'list')

  const orgFilter = organization.length > 0 ? organization[0].value : undefined

  const { data: templates, isLoading } = useQuery(
    exportTemplateQueries.list({
      filters: {
        ...(orgFilter ? { organization: orgFilter } : {}),
        ...(search ? { search } : {}),
      },
    }),
  )

  const { data: organizations, isLoading: loadingOrganizations } = useQuery(
    organizationQueries.list(),
  )

  const { requestDelete, dialogProps } = useDeleteWithConfirmation({
    mutationFn: exportTemplatesApi.remove,
    queryKey: exportTemplateQueries.all(),
  })

  const { mutate: duplicateTemplate } = useMutation({
    mutationFn: async (template: { name: string; organization: string; columnConfig: any[] }) => {
      return exportTemplatesApi.create({
        name: `Copy of ${template.name}`,
        organization: template.organization,
        columnConfig: template.columnConfig,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: exportTemplateQueries.all() })
    },
  })

  const orgOpts = useMemo(
    () =>
      organizations?.results.map((org) => ({
        label: org.name,
        value: org.id,
      })) || [],
    [organizations?.results],
  )

  return (
    <>
      <PageHeader
        title={t('exportTemplates.title')}
        actions={
          <>
            <Button
              variant="secondary"
              icon={<ArrowLeftIcon />}
              onClick={() => navigate('/expense-reports')}
            >
              {t('exportTemplates.backToReports')}
            </Button>
            <Button
              variant="primary"
              icon={<PlusIcon />}
              onClick={() => navigate('/export-templates/template')}
            >
              {t('exportTemplates.newTemplate')}
            </Button>
          </>
        }
      >
        <input
          type="text"
          placeholder={t('exportTemplates.searchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full rounded-full border border-border-strong px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-400"
          data-testid="template-search-input"
        />
      </PageHeader>
      <MainContent>
        <FilterPane title={t('exportTemplates.filtersTitle')} onClear={() => setOrganization([])}>
          <h3 className="mb-2 mt-4 text-base font-semibold">{t('exportTemplates.organization')}</h3>
          <CustomSelect
            options={orgOpts}
            values={organization}
            onChange={(values) => setOrganization(values)}
            clearable
            loading={loadingOrganizations}
          />
        </FilterPane>
        <ContentPane>
          <ListToolbar
            count={templates?.count}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
          />
          {isLoading && <p>{t('common.loading')}</p>}
          {templates && templates.results.length > 0 && (
            <ItemCollection viewMode={viewMode}>
              {templates.results.map((template) => (
                <ExportTemplateItem
                  key={template.id}
                  template={template}
                  viewMode={viewMode}
                  onOpen={(tpl) => navigate(`/export-templates/template/${tpl.id}`)}
                  onDuplicate={(tpl) =>
                    duplicateTemplate({
                      name: tpl.name,
                      organization: tpl.organization,
                      columnConfig: tpl.columnConfig,
                    })
                  }
                  onDelete={(tpl) => requestDelete(tpl.id, tpl.name)}
                />
              ))}
            </ItemCollection>
          )}
          {templates && templates.results.length === 0 && (
            <EmptyState
              title={t('exportTemplates.emptyState')}
              className="py-8"
              assistantHint={
                <AssistantHint
                  id="export-templates-empty"
                  text={t('assistantHint.exportTemplates')}
                />
              }
              data-testid="export-templates-empty-state"
            />
          )}
        </ContentPane>
      </MainContent>

      <ConfirmDeleteDialog {...dialogProps} />
    </>
  )
}
